import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import ConfirmModal from '../../components/ConfirmModal';
import ElectionNominations from '../../components/ElectionNominations';
import ElectionNdas from '../../components/ElectionNdas';

const ElectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [isAddingPosition, setIsAddingPosition] = useState(false);
  const [isUpdatingElection, setIsUpdatingElection] = useState(false);
  const [isDeletingPosition, setIsDeletingPosition] = useState(false);
  const [positionForm, setPositionForm] = useState({
    name: '',
    description: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'draft',
  });
  // const [showConfigModal, setShowConfigModal] = useState(false);
  // const [configForm, setConfigForm] = useState({
  //   multiVoteEnabled: false,
  //   votePrice: '0.00',
  //   currency: 'UGX',
  //   maxVotesPerUser: 1,
  //   resultsVisibility: 'public',
  //   emailNotificationsEnabled: false,
  // });
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSchoolOrg, setIsSchoolOrg] = useState(false);

  useEffect(() => {
    fetchElection();
    fetchOrgType();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchElection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setElection(response.data.data);
      setEditForm({
        name: response.data.data.name,
        description: response.data.data.description || '',
        startDate: response.data.data.startDate?.slice(0, 16),
        endDate: response.data.data.endDate?.slice(0, 16),
        status: response.data.data.status,
        multiVoteEnabled: response.data.data.multiVoteEnabled || false,
        votePrice: response.data.data.votePrice || '',
        currency: response.data.data.currency || 'UGX',
        maxVotesPerUser: response.data.data.maxVotesPerUser || '',
        firstVoteFree: response.data.data.firstVoteFree !== false, // Default to true
        resultsVisibility: response.data.data.resultsVisibility || '',
        emailNotificationsEnabled: response.data.data.emailNotificationsEnabled || false,
        allowGuestVoting: response.data.data.allowGuestVoting || false,
      });
    } catch (error) {
      console.error('Failed to fetch election:', error);
      const message = error.response?.data?.error || 'Failed to load election';
      showToast(message, 'error');
      if (error.response?.status === 402) {
        navigate('/admin/elections', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgType = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const orgId = userRes.data.data?.organizationId;
      if (orgId) {
        const orgRes = await axios.get(`${process.env.REACT_APP_API_URL}/organizations/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsSchoolOrg(orgRes.data.data?.orgType === 'school');
      }
    } catch (err) {
      console.error('Failed to fetch org type:', err);
    }
  };

  const handleAddPosition = async (e) => {
    e.preventDefault();
    
    // Check position limit if pricing package exists
    if (election.pricingPackage?.maxPositions) {
      const currentPositions = election.positions?.length || 0;
      if (currentPositions >= election.pricingPackage.maxPositions) {
        showToast(`Position limit reached (${election.pricingPackage.maxPositions}). Upgrade your package to add more.`, 'error');
        return;
      }
    }
    
    setIsAddingPosition(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/positions`,
        { ...positionForm, electionId: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Category added successfully', 'success');
      setShowPositionModal(false);
      setPositionForm({ name: '', description: '' });
      fetchElection();
    } catch (error) {
      console.error('Failed to add category:', error);
      showToast(error.response?.data?.error || 'Failed to add category', 'error');
    } finally {
      setIsAddingPosition(false);
    }
  };

  const handleUpdateElection = async (e) => {
    e.preventDefault();
    setIsUpdatingElection(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/elections/${id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Election updated successfully', 'success');
      setShowEditModal(false);
      fetchElection();
    } catch (error) {
      console.error('Failed to update election:', error);
      showToast(error.response?.data?.error || 'Failed to update election', 'error');
    } finally {
      setIsUpdatingElection(false);
    }
  };

  const handleDeletePositionClick = (position) => {
    setPositionToDelete(position);
    setShowDeleteModal(true);
  };

  const handleDeletePositionConfirm = async () => {
    setIsDeletingPosition(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/positions/${positionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Category deleted successfully', 'success');
      setShowDeleteModal(false);
      setPositionToDelete(null);
      fetchElection();
    } catch (error) {
      console.error('Failed to delete category:', error);
      showToast('Failed to delete category', 'error');
    } finally {
      setIsDeletingPosition(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'secondary',
      active: 'success',
      completed: 'info',
      cancelled: 'danger',
    };
    return `badge bg-${badges[status] || 'secondary'}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url || getPublicElectionUrl()).then(() => {
      setCopySuccess(true);
      showToast('Link copied to clipboard!', 'success');
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      showToast('Failed to copy link', 'error');
    });
  };

  const getPublicElectionUrl = () => {
    return `${window.location.origin}/elections/${id}`;
  };

  const getIdVotingUrl = () => {
    return `${window.location.origin}/vote/id/${id}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="text-center py-5">
        <p>Election not found</p>
        <Link to="/admin/elections" className="btn btn-primary">
          Back to Elections
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Election Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Link to="/admin/elections" style={{ color: '#6b7280', textDecoration: 'none' }}>
              <i className="bi bi-arrow-left"></i> Back
            </Link>
          </div>
          <h3 className="mb-1" style={{ fontWeight: '600' }}>{election.name}</h3>
          <span className={getStatusBadge(election.status)} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
            {election.status}
          </span>
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={() => setShowShareModal(true)}
            className="btn btn-sm btn-outline-primary"
            style={{
              padding: '0.375rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            <i className="bi bi-share me-2"></i>
            Share Link
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="btn btn-sm"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '0.375rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            <i className="bi bi-pencil me-2"></i>
            Edit
          </button>
        </div>
      </div>

      {/* Election Info Card */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Description</label>
                    <p style={{ fontSize: '0.875rem' }}>{election.description || 'No description provided'}</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Start Date</label>
                    <p style={{ fontSize: '0.875rem' }}>{formatDate(election.startDate)}</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>End Date</label>
                    <p style={{ fontSize: '0.875rem' }}>{formatDate(election.endDate)}</p>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>Access Type</label>
                    <p style={{ fontSize: '0.875rem' }}>
                      <span className={`badge ${election.accessType === 'open' ? 'bg-success' : 'bg-info'}`}>
                        {election.accessType === 'open' ? (
                          <><i className="bi bi-globe me-1"></i>Open</>
                        ) : (
                          <><i className="bi bi-lock me-1"></i>Closed</>
                        )}
                      </span>
                    </p>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {election.accessType === 'open' ? 'Anyone can vote' : 'Members only'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Package Quota Display */}
      {election.pricingPackage && (
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card" style={{ border: 'none', borderLeft: '4px solid #667eea' }}>
              <div className="card-body">
                <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>
                  <i className="bi bi-tags me-2"></i>
                  {election.pricingPackage.name} Package
                </h6>
                <div className="row">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>POSITIONS</div>
                    <div className="progress" style={{ height: '6px' }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: `${election.pricingPackage.maxPositions ? ((election.positions?.length || 0) / election.pricingPackage.maxPositions * 100) : 0}%`,
                          backgroundColor: '#667eea'
                        }}
                      ></div>
                    </div>
                    <div className="mt-1" style={{ fontSize: '0.875rem' }}>
                      {election.positions?.length || 0} / {election.pricingPackage.maxPositions || 'Unlimited'} used
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>VOTERS</div>
                    {election.pricingPackage.maxVoters ? (
                      <>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${((election.voterCount || 0) / election.pricingPackage.maxVoters * 100)}%`,
                              backgroundColor: (election.voterCount || 0) >= election.pricingPackage.maxVoters ? '#ef4444' : '#667eea'
                            }}
                          ></div>
                        </div>
                        <div className="mt-1" style={{ fontSize: '0.875rem' }}>
                          {election.voterCount || 0} / {election.pricingPackage.maxVoters} voted
                          {(election.voterCount || 0) >= election.pricingPackage.maxVoters && (
                            <span className="text-danger ms-2">
                              <i className="bi bi-exclamation-triangle"></i> Limit reached
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: '0.875rem' }}>
                        {election.voterCount || 0} voters (Unlimited)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ElectionNominations election={election} onCandidatesChanged={fetchElection} />
      <ElectionNdas electionId={id} />

      {/* Categories */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1.1rem' }}>Categories</h5>
        <button
          onClick={() => setShowPositionModal(true)}
          className="btn"
          disabled={election.pricingPackage?.maxPositions && (election.positions?.length || 0) >= election.pricingPackage.maxPositions}
          style={{
            background: election.pricingPackage?.maxPositions && (election.positions?.length || 0) >= election.pricingPackage.maxPositions 
              ? '#9ca3af' 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            borderRadius: '8px',
            cursor: election.pricingPackage?.maxPositions && (election.positions?.length || 0) >= election.pricingPackage.maxPositions 
              ? 'not-allowed' 
              : 'pointer',
          }}
          title={election.pricingPackage?.maxPositions && (election.positions?.length || 0) >= election.pricingPackage.maxPositions 
            ? 'Position limit reached. Upgrade your package to add more.' 
            : 'Add a new category'}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add Category
        </button>
      </div>
      <div className="card" style={{ border: 'none' }}>
        <div className="card-body p-0">
          {!election.positions || election.positions.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-trophy" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No categories yet. Add your first category.</p>
            </div>
          ) : (
            election.positions.map((position, idx) => (
              <Link
                key={position.id}
                to={`/admin/positions/${position.id}/candidates`}
                className="d-flex justify-content-between align-items-center px-4 py-3 text-decoration-none"
                style={{ borderBottom: idx < election.positions.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.15s', color: 'inherit' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#667eea15', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', flexShrink: 0 }}>
                    <i className="bi bi-trophy" style={{ color: '#667eea', fontSize: '1.1rem' }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1e293b' }}>{position.name}</div>
                    {position.description && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '350px' }}>
                        {position.description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
                  <div className="text-center" style={{ minWidth: '50px' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#2c3e50' }}>{position.candidates?.length || 0}</div>
                    <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>candidates</div>
                  </div>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeletePositionClick(position); }}
                    style={{ color: '#d1d5db', background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer', fontSize: '1rem', transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                  <i className="bi bi-chevron-right" style={{ color: '#d1d5db', fontSize: '0.9rem' }}></i>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      {showPositionModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Category</h5>
                <button type="button" className="btn-close" onClick={() => setShowPositionModal(false)}></button>
              </div>
              <form onSubmit={handleAddPosition}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Category Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={positionForm.name}
                      onChange={(e) => setPositionForm({ ...positionForm, name: e.target.value })}
                      placeholder="e.g., President, Secretary, Best Artist"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={positionForm.description}
                      onChange={(e) => setPositionForm({ ...positionForm, description: e.target.value })}
                      placeholder="Describe the role and responsibilities"
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPositionModal(false)} disabled={isAddingPosition}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isAddingPosition}>
                    {isAddingPosition ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Adding...
                      </>
                    ) : (
                      'Add Category'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Election Modal */}
      {showEditModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Election</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleUpdateElection}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Start Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editForm.startDate}
                      onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">End Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editForm.endDate}
                      onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Only show voting config for open elections */}
                  {election.accessType === 'open' && (
                    <>
                      <hr className="my-4" />
                      <h6 className="mb-3" style={{ fontWeight: '600', fontSize: '0.875rem', color: '#4b5563' }}>Voting Configuration (Optional)</h6>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>Set default voting rules for all positions. Leave blank to configure per position.</p>
                      
                      <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="editMultiVote"
                        checked={editForm.multiVoteEnabled || false}
                        onChange={(e) => setEditForm({ ...editForm, multiVoteEnabled: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="editMultiVote">
                        Enable Multiple Voting
                      </label>
                    </div>
                  </div>

                  {editForm.multiVoteEnabled && (
                    <>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Vote Price</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            value={editForm.votePrice || ''}
                            onChange={(e) => setEditForm({ ...editForm, votePrice: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Currency</label>
                          <select
                            className="form-select"
                            value={editForm.currency || 'UGX'}
                            onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                          >
                            <option value="UGX">UGX (Ugandan Shilling)</option>
                            <option value="USD">USD</option>
                            <option value="GBP">GBP</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Max Votes Per User</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editForm.maxVotesPerUser || ''}
                          onChange={(e) => setEditForm({ ...editForm, maxVotesPerUser: e.target.value })}
                          placeholder="Leave empty for unlimited"
                        />
                      </div>
                      {!editForm.allowGuestVoting && (
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFirstVoteFree"
                            checked={editForm.firstVoteFree === true}
                              onChange={(e) => setEditForm({ ...editForm, firstVoteFree: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="editFirstVoteFree">
                              First Vote Free
                            </label>
                          </div>
                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                            When enabled, the first vote is free and additional votes require payment. When disabled, ALL votes (including the first) require payment.
                          </small>
                        </div>
                      )}
                    </>
                  )}

                  {/* Guest Voting */}
                  {election.accessType === 'open' && editForm.votePrice > 0 && (
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="editAllowGuestVoting"
                          checked={editForm.allowGuestVoting || false}
                          onChange={(e) => setEditForm({ ...editForm, allowGuestVoting: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="editAllowGuestVoting">
                          <i className="bi bi-phone me-1"></i>
                          Allow Guest Voting (Phone Number)
                        </label>
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Allow non-registered users to vote using their phone number. Guests must pay for each vote. Only available for public elections with vote price.
                      </small>
                    </div>
                  )}
                    </>
                  )}

                  {/* Kiosk PIN (for ID-based elections) */}
                  {(election.votingMode === 'id_based' || editForm.votingMode === 'id_based') && (
                    <div className="mb-3">
                      <label className="form-label">Kiosk PIN <small className="text-muted">(4-digit PIN to end kiosk voting sessions)</small></label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.kioskPin || ''}
                        onChange={(e) => setEditForm({ ...editForm, kioskPin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        placeholder="e.g. 1234"
                        maxLength={4}
                        style={{ maxWidth: '150px', letterSpacing: '4px', textAlign: 'center' }}
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Results Visibility</label>
                    <select
                      className="form-select"
                      value={editForm.resultsVisibility || ''}
                      onChange={(e) => setEditForm({ ...editForm, resultsVisibility: e.target.value })}
                    >
                      <option value="">Not set (configure per position)</option>
                      <option value="public">Public (anyone can view)</option>
                      <option value="voters_only">Voters Only</option>
                      <option value="hidden">Hidden (admins only)</option>
                      <option value="after_voting">After Voting Ends</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="editEmailNotifications"
                        checked={editForm.emailNotificationsEnabled || false}
                        onChange={(e) => setEditForm({ ...editForm, emailNotificationsEnabled: e.target.checked })}
                        disabled
                      />
                      <label className="form-check-label" htmlFor="editEmailNotifications" style={{ opacity: 0.6 }}>
                        Enable Email Notifications
                      </label>
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Send vote confirmation emails to voters after they cast their vote. <span style={{ color: '#667eea', fontWeight: '500' }}>Premium feature</span>
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={isUpdatingElection}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isUpdatingElection}>
                    {isUpdatingElection ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Election'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Position Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Position"
        message={`Are you sure you want to delete the position "${positionToDelete?.name}"? This will also remove all candidates for this position.`}
        onConfirm={handleDeletePositionConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setPositionToDelete(null);
        }}
        confirmText={isDeletingPosition ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        isLoading={isDeletingPosition}
      />

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-share me-2"></i>
                  Share Election Link
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowShareModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* ID-Based Voting Link (for id_based elections or school organizations) */}
                {(election.votingMode === 'id_based' || isSchoolOrg) && (
                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      <i className="bi bi-person-badge me-1"></i> Voter ID Voting Link
                    </label>
                    <p className="mb-2" style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                      Share this link with students — they enter their Voter ID to vote.
                    </p>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={getIdVotingUrl()}
                        readOnly
                        style={{ fontSize: '0.875rem', backgroundColor: '#f0fdf4' }}
                      />
                      <button
                        className="btn btn-success"
                        onClick={() => handleCopyLink(getIdVotingUrl())}
                      >
                        <i className="bi bi-clipboard me-1"></i> Copy
                      </button>
                    </div>
                  </div>
                )}

                {/* Public Election Link — only for open elections */}
                {election.accessType !== 'closed' && (
                  <div>
                    <label className="form-label" style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      <i className="bi bi-globe me-1"></i> Public Election Link
                    </label>
                    <p className="mb-2" style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                      Share this link to allow voters to view the election details and candidates.
                    </p>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={getPublicElectionUrl()}
                        readOnly
                        style={{ fontSize: '0.875rem', backgroundColor: '#f9fafb' }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={() => handleCopyLink(getPublicElectionUrl())}
                        style={{
                          background: copySuccess ? '#10b981' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <i className={`bi ${copySuccess ? 'bi-check-lg' : 'bi-clipboard'} me-2`}></i>
                        {copySuccess ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Email invitation note — for standard closed elections (non-school) */}
                {election.accessType === 'closed' && election.votingMode !== 'id_based' && !isSchoolOrg && (
                  <div className="mb-4 p-3" style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <label className="form-label mb-2" style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      <i className="bi bi-envelope me-1"></i> Voting Links (Email)
                    </label>
                    <p className="mb-2" style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                      This closed election uses unique voting links sent to each member by email.
                    </p>
                    <Link to="/admin/members" className="btn btn-sm btn-success">
                      <i className="bi bi-send me-1"></i> Send Voting Links from Voters Page
                    </Link>
                  </div>
                )}

                {/* Spectator Link (for closed elections) */}
                {election.accessType === 'closed' && election.spectatorCode && (
                  <div className="mt-4">
                    <label className="form-label" style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      <i className="bi bi-eye me-1"></i> Spectator Link <span className="badge bg-secondary" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>Read-only</span>
                    </label>
                    <p className="mb-2" style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                      Share with parents or observers to follow live results without voting.
                    </p>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={`${window.location.origin}/elections/${id}/spectator?code=${election.spectatorCode}`}
                        readOnly
                        style={{ fontSize: '0.875rem', backgroundColor: '#fdf4ff' }}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handleCopyLink(`${window.location.origin}/elections/${id}/spectator?code=${election.spectatorCode}`)}
                      >
                        <i className="bi bi-clipboard me-1"></i> Copy
                      </button>
                    </div>
                    <div className="mt-2" style={{ fontSize: '0.75rem', color: '#9333ea' }}>
                      <i className="bi bi-key me-1"></i> Spectator Code: <strong>{election.spectatorCode}</strong>
                    </div>
                  </div>
                )}

                <div className="mt-3 p-3" style={{ backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-info-circle me-2" style={{ color: '#3b82f6', fontSize: '1.125rem' }}></i>
                    <small style={{ color: '#1e40af', fontSize: '0.813rem' }}>
                      {(election.votingMode === 'id_based' || isSchoolOrg)
                        ? 'Use the Voter ID link for kiosk/shared-device voting. Students enter their Voter ID to authenticate and vote, then switch to the next voter.'
                        : election.accessType === 'closed'
                          ? 'Standard closed elections use individual email voting links. Send them from the Voters page.'
                          : 'This public link allows anyone to view the election details and candidates. Voters will need to log in to cast their votes.'}
                      {election.accessType === 'closed' && ' The spectator link lets observers view live results without the ability to vote.'}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectionDetail;
